import React from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import PropTypes from 'prop-types';
import useContributionDialog from '@/components/common/contribution/useContributionDialog';
import CustomDialog from '@/components/common/CustomDialog';
import InteractionDialog from '@/components/common/interaction/InteractionDialog';
import MultiActionDialogKey from '@/constants/MultiActionDialogKey';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import UserViewEnums from '@/constants/users/UserViewEnums';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import { useCurrentUser } from '@/lib/hooks/userData';
import useRecentlyViewedConstituents from '@/lib/hooks/useRecentlyViewedConstituents';
import translate from '@/lib/intl/translate';
import ConstituentPropType from '@/lib/types/ConstituentPropType';
import { ConstituentCardItemActionsAssign } from '@/modules/ConstituentDetails';
import { Dialogs, useDialogManager } from '@/modules/Dialogs';
import { deleteConstituent } from '@/services/constituentServices';
import showErrorMessage from '@/utils/toasts/showErrorMessage';
import showSuccessMessage from '@/utils/toasts/showSuccessMessage';

const FollowUpAction = ({ action, onClose, constituent }) => {
  let content;
  const { constituentId, displayName } = constituent;

  switch (action) {
    case MultiActionDialogKey.INTERACTION:
      content = (
        <InteractionDialog
          onClose={onClose}
          preSelectedDonorId={constituentId}
        />
      );
      break;
    case MultiActionDialogKey.ASSIGN:
      content = (
        <ConstituentCardItemActionsAssign
          constituent={constituent}
          header={<div className="px-4 pt-4 type-h2">{displayName}</div>}
          onClose={onClose}
        />
      );
      break;
    default:
      content = null;
      break;
  }

  return content;
};

function ConstituentInteractionMenu({ constituent, onClose, open, target }) {
  const [selectedAction, setSelectedAction] = React.useState(null);
  const history = useHistory();
  const { views, isSuperAdmin } = useCurrentUser(true);
  const { openDialog, closeDialog } = useDialogManager();
  const ownerOrgId = useOwnerOrgId();
  const { deleteConstituentFromView } = useRecentlyViewedConstituents();

  const closeAction = () => {
    setSelectedAction(null);
    onClose();
  };

  const { constituentId } = constituent;

  // Contribution form is now using new DialogManagerProvider so launch with that.
  useContributionDialog(selectedAction === MultiActionDialogKey.DONATION, {
    onClose: closeAction,
    preSelectedDonorId: constituentId,
  });

  const menuItems = [
    {
      label: translate('DONOR_BOARD_ACTION_INTERACTION'),
      key: MultiActionDialogKey.INTERACTION,
    },
    {
      label: translate('DONOR_BOARD_ACTION_CONTRIBUTION'),
      key: MultiActionDialogKey.DONATION,
    },
    {
      label: translate('DONOR_BOARD_ACTION_UPDATE_ASSIGNED'),
      key: MultiActionDialogKey.ASSIGN,
    },
    {
      label: translate('DONOR_BOARD_ACTION_EDIT_PROFILE'),
      key: MultiActionDialogKey.EDIT,
      onClick: () => {
        const path = generatePath(AuthRoutes.CONSTITUENT_EDIT, {
          constituentId,
        });
        history.push(path);
      },
    },
  ];

  if (isSuperAdmin || views?.includes(UserViewEnums.ADMIN)) {
    menuItems.push({
      label: (
        <span className="text-error-main">
          {translate('DELETE_CONSTITUENT')}
        </span>
      ),
      key: MultiActionDialogKey.DELETE_CONSTITUENT,
      onClick: () => {
        openDialog(Dialogs.CONFIRMATION, {
          children: translate('DELETE_CONSTITUENT_CONFIRMATION_MESSAGE'),
          confirmLabel: translate('DELETE'),
          headerText: translate('DELETE_CONSTITUENT'),
          onCancel: closeDialog,
          onClose: closeDialog,
          onConfirm: async () => {
            try {
              await deleteConstituent({ constituentId, ownerOrgId });
              deleteConstituentFromView(constituentId);
              showSuccessMessage({
                message: translate('DELETE_CONSTITUENT_SUCCESS_MESSAGE'),
              });
            } catch {
              showErrorMessage({
                message: translate('DELETE_CONSTITUENT_ERROR_MESSAGE', {
                  name: constituent.displayName,
                }),
              });
            } finally {
              closeDialog();
              closeAction();
            }
          },
        });
      },
    });
  }

  return (
    <>
      <Menu onClose={onClose} open={open} anchorEl={target}>
        {menuItems.map(({ label, key, onClick }) => (
          <MenuItem
            key={key}
            onClick={() => {
              if (onClick) {
                onClick();
              } else {
                setSelectedAction(key);
              }
            }}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>

      {selectedAction !== MultiActionDialogKey.DONATION && (
        <CustomDialog
          fullWidth
          maxWidth="sm"
          open={selectedAction}
          onClose={closeAction}
          scroll="paper"
          showDialogActions={false}
        >
          <FollowUpAction
            action={selectedAction}
            onClose={closeAction}
            constituent={constituent}
          />
        </CustomDialog>
      )}
    </>
  );
}

ConstituentInteractionMenu.propTypes = {
  constituent: ConstituentPropType,
  onClose: PropTypes.func,
  open: PropTypes.bool,
  target: PropTypes.object,
};

ConstituentInteractionMenu.defaultProps = {
  constituent: null,
  onClose: () => {},
  open: false,
  target: null,
};

export default ConstituentInteractionMenu;
